import { dec, type Dec } from '@praticheenergia/ad1-engine'

export interface MeseSerie {
  readonly anno: number
  readonly mese: number
  /** kWh come stringa decimale; null se il mese non e' stato scaricato. */
  readonly produzione: string | null
}

export type StatoScostamento = 'ok' | 'anomalia' | 'senza_storico' | 'senza_dati'

export interface Scostamento {
  readonly stato: StatoScostamento
  readonly ultimoMese: { anno: number; mese: number } | null
  readonly produzione12Mesi: Dec | null
  /** Percentuale, ultimi 12 mesi contro i 12 precedenti; null senza un anno intero di storico. */
  readonly variazione: Dec | null
  /** Percentuale, ultimo mese contro lo stesso mese dell'anno prima. */
  readonly variazioneMese: Dec | null
}

const indice = (anno: number, mese: number) => anno * 12 + (mese - 1)

function percentuale(attuale: Dec, precedente: Dec): Dec | null {
  if (precedente.isZero()) return null
  return attuale.minus(precedente).div(precedente).times(100).toDecimalPlaces(1)
}

/**
 * Confronto anno su anno, mai mese su mese: la stagionalita' del fotovoltaico renderebbe
 * il secondo puro rumore (spec SP6 §6). Senza lo stesso mese dell'anno prima l'allarme e'
 * sospeso: un impianto nuovo non deve generare falsi allarmi.
 */
export function valutaScostamento(serie: readonly MeseSerie[], soglia: string | number): Scostamento {
  const perMese = new Map<number, Dec>()
  for (const m of serie) if (m.produzione !== null) perMese.set(indice(m.anno, m.mese), dec(m.produzione))
  if (perMese.size === 0) {
    return { stato: 'senza_dati', ultimoMese: null, produzione12Mesi: null, variazione: null, variazioneMese: null }
  }

  const ultimo = Math.max(...perMese.keys())
  const ultimoMese = { anno: Math.floor(ultimo / 12), mese: (ultimo % 12) + 1 }
  const finestra = (fine: number) => {
    const valori = Array.from({ length: 12 }, (_, i) => perMese.get(fine - i))
    return valori.every((v) => v !== undefined) ? valori.reduce((t, v) => t.plus(v!), dec(0)) : null
  }
  const somma12 = Array.from({ length: 12 }, (_, i) => perMese.get(ultimo - i)).reduce<Dec>(
    (t, v) => (v === undefined ? t : t.plus(v)),
    dec(0),
  )

  const annoPrima = perMese.get(ultimo - 12)
  const variazioneMese = annoPrima === undefined ? null : percentuale(perMese.get(ultimo)!, annoPrima)
  const attuali = finestra(ultimo)
  const precedenti = finestra(ultimo - 12)
  const variazione = attuali && precedenti ? percentuale(attuali, precedenti) : null

  const limite = dec(soglia).negated()
  const sotto = (v: Dec | null) => v !== null && v.lessThan(limite)
  const stato: StatoScostamento =
    variazioneMese === null && variazione === null
      ? 'senza_storico'
      : sotto(variazioneMese) || sotto(variazione)
        ? 'anomalia'
        : 'ok'

  return { stato, ultimoMese, produzione12Mesi: somma12, variazione, variazioneMese }
}
